
'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import { useAuth } from './providers/auth-provider';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from './ui/button';
import { Users, Compass, LayoutGrid, User, Camera, LogIn, Info } from 'lucide-react';
import { useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '@/lib/firebase';

const CAPTURED_IMAGE_KEY = 'captured-artwork-image';
const googleProvider = new GoogleAuthProvider();

export function MobileNav() {
    const pathname = usePathname();
    const router = useRouter();
    const { user, loading } = useAuth();
    const isMobile = useIsMobile();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    if (!isMobile) {
        return null;
    }

    const handleSignIn = async () => {
        try {
          await signInWithPopup(auth, googleProvider);
          router.push('/my-collection');
        } catch (error) {
          console.error("Error signing in with Google", error);
          toast({
            title: "Login Failed",
            description: "There was an error signing in. Please try again.",
            variant: "destructive",
          })
        }
    };
    
    const handleCameraClick = () => {
        if (loading) {
            return;
        }

        if (user) {
            fileInputRef.current?.click();
        } else {
            toast({
                title: 'Login Required',
                description: 'Please log in to add an artwork.',
                variant: 'destructive',
            });
        }
    }

    const fileToDataUri = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                const dataUri = await fileToDataUri(file);
                sessionStorage.setItem(CAPTURED_IMAGE_KEY, dataUri);
                router.push('/artworks/new');
            } catch (error) {
                console.error("Failed to process image", error);
                toast({
                    title: 'Error',
                    description: 'Could not process the captured image. Please try again.',
                    variant: 'destructive'
                });
            }
        }
    };


    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 h-24 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t">
            <div className="container h-full px-2">
                <div className="flex h-full items-center justify-between">
                    
                    <Link href="/discover" className={cn("flex flex-col items-center gap-1 text-xs text-muted-foreground w-16", pathname === '/discover' && "text-primary")}>
                        <Compass className="h-5 w-5" />
                        <span>Discover</span>
                    </Link>
                    <Link href="/artists" className={cn("flex flex-col items-center gap-1 text-xs text-muted-foreground w-16", pathname === '/artists' && "text-primary")}>
                        <Users className="h-5 w-5" />
                        <span>Artists</span>
                    </Link>
                    
                    <div className="self-start">
                        <Button 
                            size="icon" 
                            className="h-20 w-20 rounded-full shadow-lg -translate-y-1 bg-gradient-to-br from-blue-500 to-purple-600 text-white transition-transform hover:scale-105"
                            onClick={handleCameraClick}
                            disabled={loading}
                        >
                            <Camera className="h-10 w-10" />
                            <span className="sr-only">Add Artwork</span>
                            <input 
                                type="file" 
                                accept="image/*" 
                                capture="environment" 
                                ref={fileInputRef} 
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </Button>
                    </div>
                    
                    <Link href="/my-collection" className={cn("flex flex-col items-center gap-1 text-xs text-center w-16", user ? "text-muted-foreground" : "text-muted-foreground/30 pointer-events-none", pathname === '/my-collection' && user && "text-primary")}>
                        <LayoutGrid className="h-5 w-5" />
                        <span>Collection</span>
                    </Link>
                    
                    
                    {loading ? (
                        <div className="w-16" aria-hidden="true" />
                    ) : user ? (
                        <Link href="/profile" className={cn("flex flex-col items-center gap-1 text-xs text-muted-foreground w-16", pathname.startsWith('/profile') && "text-primary")}>
                            <Avatar className="h-6 w-6">
                                <AvatarImage src={user.photoURL ?? ''} alt={user.displayName ?? 'Profile'} />
                                <AvatarFallback>
                                    <User className="h-4 w-4" />
                                </AvatarFallback>
                            </Avatar>
                            <span>Profile</span>
                        </Link>
                    ) : (
                         <button onClick={handleSignIn} className="flex flex-col items-center gap-1 text-xs text-muted-foreground w-16">
                            <LogIn className="h-5 w-5" />
                            <span>Login</span>
                        </button>
                    )}

                </div>
            </div>
        </div>
    );
}
